
import { useProductMeta } from '@/hooks/meta/useProductMeta';
import { useProjectMeta } from '@/hooks/meta/useProjectMeta';
import { useProjectStore } from '@/hooks/meta/useProjectStore';
import { useStoriesByProject } from '@/hooks/useStories';
import { useEffect, useMemo } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

interface AppContextType {
    tenantId: string | null;
    projectId: string | null;
    storyId: string | null;
    chapterId: string | null;
    entityId: string | null;
    isLoading: boolean;
    activeStoryId: string | null;
    isStoryLoading: boolean;
}

// Re-exporting as a hook instead of a context
export function useApp(): AppContextType {
    const location = useLocation();

    // Get tenant info from useProductMeta which handles fetching
    const { tenant, isLoading: isTenantLoading } = useProductMeta();
    const tenantId = tenant?.id || null;

    const { setActiveProject, setActiveStory, setActiveChapter, activeProjectId: storedPid, activeStoryId: storedSid } = useProjectStore();


    // Derive IDs from URL
    const { projectId, storyId, chapterId, entityId } = useMemo(() => {
        const pathname = location.pathname;

        // Helper to match multiple patterns
        const match = (pattern: string) => matchPath({ path: pattern, end: false }, pathname);

        // Project ID
        const projectMatch = match("/projects/:projectId");
        const projectId = projectMatch?.params.projectId || null;

        // Story ID
        const storyMatch = match("/projects/:projectId/stories/:storyId");
        const storyId = storyMatch?.params.storyId || null;

        // Chapter ID - can be under project or story or standalone
        const chapterMatch =
            match("/projects/:projectId/chapters/:chapterId") ||
            match("/projects/:projectId/stories/:storyId/chapters/:chapterId") ||
            match("/chapters/:chapterId");
        const chapterId = chapterMatch?.params.chapterId || null;

        // Entity ID - can be under project or story
        const entityMatch =
            match("/projects/:projectId/entities/:entityId") ||
            match("/projects/:projectId/story/:storyId/entity/:entityId") ||
            match("/projects/:projectId/entity/:entityId");
        const entityId = entityMatch?.params.entityId || null;

        return {
            projectId,
            storyId,
            chapterId,
            entityId
        };
    }, [location.pathname]);

    // Update store when IDs are present in URL
    useEffect(() => {
        if (projectId) {
            // If the project ID in the URL is different from the stored one,
            // we must clear the nested story/chapter IDs to prevent context leakage.
            if (storedPid && projectId !== storedPid) {
                setActiveStory(null);
                setActiveChapter(null);
            }
            setActiveProject(projectId);
        }
        if (storyId) setActiveStory(storyId);
        if (chapterId) setActiveChapter(chapterId);
    }, [projectId, storyId, chapterId, storedPid, setActiveProject, setActiveStory, setActiveChapter]);

    // Fetch current story for the project
    const effectiveProjectId = projectId || storedPid || null;
    const { currentStoryId, isLoading: isProjectLoading } = useProjectMeta(effectiveProjectId);

    // If no currentStoryId in project meta, try to find any story for this project
    const { data: projectStories, isLoading: isStoriesLoading } = useStoriesByProject(
        effectiveProjectId || undefined, 
        { enabled: !!effectiveProjectId && !currentStoryId && !storedSid }
    );

    const firstStoryId = projectStories && projectStories.length > 0 ? projectStories[0].id : null;
    const isStoryLoading = isProjectLoading || (!!effectiveProjectId && !currentStoryId && !storedSid && isStoriesLoading);
    const activeStoryId = currentStoryId || firstStoryId || null;

    const isLoading = isTenantLoading;
    
    // Safety check: Don't use storedSid if we are currently in a different project context from the URL
    const isProjectMismatch = projectId && storedPid && projectId !== storedPid;
    const effectiveStoryId = storyId || (isProjectMismatch ? null : storedSid) || activeStoryId;

    // Auto-set the active story if it's missing in the store but available via fallback
    useEffect(() => {
        if (!storedSid && effectiveStoryId) {
            setActiveStory(effectiveStoryId);
        }
    }, [storedSid, effectiveStoryId, setActiveStory]);

    console.log("[useApp] resolve:", {
        urlPid: projectId,
        storedPid,
        urlSid: storyId,
        storedSid,
        metaSid: currentStoryId,
        fallbackSid: firstStoryId,
        effectivePid: effectiveProjectId,
        effectiveSid: effectiveStoryId
    });

    return {
        tenantId,
        projectId: effectiveProjectId,
        storyId: effectiveStoryId,
        chapterId,
        entityId,
        isLoading,
        activeStoryId: activeStoryId, // Best available fallback
        isStoryLoading
    };
}


